import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { CAVE_GAMES } from "./CaveGames";
import { pick, CVC_WORDS, DIGRAPH_ROUNDS, SIGHT_WORDS } from "../../data/phonicsContent";
import { sfx } from "../../services/soundfx";
import { speak } from "../../services/phonicsAudio";

const STEPS = 5;
const PASS = 0.7; // must score >= 70% to pass a cave

function buildSession(cave, band) {
  const cvcWords = CVC_WORDS.map((w) => w.word);
  return Array.from({ length: STEPS }).map(() => {
    const type = pick(band.pool);
    const Comp = CAVE_GAMES[type];
    const key = Math.random().toString(36).slice(2);
    if (band.mode === "letters") {
      return { Comp, props: { sound: pick(cave.sounds), allLetters: cave.sounds }, key };
    }
    if (band.mode === "words") {
      return { Comp, props: { word: pick(cvcWords), words: cvcWords }, key };
    }
    // advanced
    if (type === "digraph") return { Comp, props: { round: pick(DIGRAPH_ROUNDS) }, key };
    if (type === "sight") return { Comp, props: { word: pick(SIGHT_WORDS), words: SIGHT_WORDS }, key };
    return { Comp, props: { word: pick(SIGHT_WORDS) }, key }; // echoword
  });
}

export default function CavePlayer({ cave, band, onComplete }) {
  const [attempt, setAttempt] = useState(0);
  const session = useMemo(() => buildSession(cave, band), [cave, band, attempt]);
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [failed, setFailed] = useState(false);

  const step = session[idx];
  const Comp = step.Comp;

  const onDone = ({ correct: wasCorrect }) => {
    const nextCorrect = correct + (wasCorrect ? 1 : 0);
    setCorrect(nextCorrect);
    if (idx + 1 >= STEPS) {
      const score = nextCorrect / STEPS;
      if (score >= PASS) {
        const stars = nextCorrect >= STEPS ? 3 : nextCorrect >= 4 ? 2 : 1;
        onComplete(stars);
      } else {
        sfx.wrong();
        speak("So close! Let's try this cave again to get it just right!");
        setFailed(true);
      }
    } else {
      setIdx((i) => i + 1);
    }
  };

  const retry = () => {
    sfx.tap();
    setFailed(false);
    setIdx(0);
    setCorrect(0);
    setAttempt((a) => a + 1);
  };

  if (failed) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-6 pt-16 no-select" data-testid="cave-retry">
        <motion.div animate={{ rotate: [-8, 8, -8] }} transition={{ repeat: Infinity, duration: 1.4 }} className="text-7xl mb-4">💪</motion.div>
        <h2 className="font-display text-3xl font-black text-amber-500 mb-2">Almost there!</h2>
        <p className="font-bold text-slate-600 max-w-sm mb-6">You need 70% to earn the crystal. Give this cave one more go — you've got this!</p>
        <button data-testid="cave-retry-btn" onClick={retry} className="pop-btn inline-flex items-center gap-2 rounded-full bg-amber-400 text-white font-display text-xl font-bold px-8 py-4 border-4 border-white shadow-[0_6px_0_#D97706] active:shadow-none">
          <RotateCcw strokeWidth={3} /> Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center px-4 pt-3 pb-10 min-h-full no-select">
      <div className="flex gap-2 mb-4">
        {session.map((_, i) => (
          <div key={i} className={`w-3.5 h-3.5 rounded-full ${i < idx ? "bg-green-500" : i === idx ? "bg-amber-400 animate-pulse" : "bg-white/70 border-2 border-slate-300"}`} />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step.key} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }} className="w-full flex justify-center">
          <Comp {...step.props} onDone={onDone} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
